import SubProductDisplay from './sub-product-display';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Board from 'src/app/model/product/board/board';
import BoardPart from 'src/app/model/product/board/board-part';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import CustomWindowsticker from 'src/app/model/product/windowsticker/custom-windowsticker';
import StandardStickerPart from 'src/app/model/product/windowsticker/standard-sticker-part';
import StickerPart from 'src/app/model/product/windowsticker/sticker-part';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import ProductHelper from 'src/app/helpers/product-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { BoardPartFormHelper } from 'src/app/shared/project-components/board-form/board-part-form/board-part-form-helper';

export default class SubProductDisplayHelper {
  private constructor() {}

  public static getSubProductDisplays(
    product: Product,
    full = true
  ): SubProductDisplay[] {
    let toReturn: SubProductDisplay[] = null;
    switch (product.type) {
      case ProductTypes.WINDOWSTICKER.value:
        toReturn = this.getWindowStickerSubProductDisplays(<Windowsticker>(
          product
        ));
        break;
      case ProductTypes.CUSTOMWINDOWSTICKER.value:
        toReturn = this.getCustomWindowStickerSubProductDisplays(<
          CustomWindowsticker
        >product);
        break;
      case ProductTypes.BOARD.value:
        toReturn = this.getBoardSubProductDisplays(<Board>product, full);
        break;
      case ProductTypes.OTHER_PRODUCT.value:
        toReturn = this.getOtherProductSubProductDisplays(<OtherProduct>(
          product
        ));
        break;
      default:
        throw new Error(`type not recognized: ${product.type}`);
    }
    CollectionHelper.filterInPlace(toReturn, e => e != null);
    return toReturn;
  }

  private static getWindowStickerSubProductDisplays(
    windowSticker: Windowsticker
  ): SubProductDisplay[] {
    return !CollectionHelper.isPopulated(windowSticker.standardStickerParts)
      ? []
      : windowSticker.standardStickerParts
          .map((sp, i) => {
            return [
              this.getStandardStickerPartFormatDisplay(
                sp,
                i,
                windowSticker.standardStickerParts.length
              ),
              this.getStandardStickerPartDescriptionDisplay(
                sp,
                i,
                windowSticker.standardStickerParts.length
              )
            ];
          })
          .flat();
  }

  private static getStandardStickerPartFormatDisplay(
    standardStickerPart: StandardStickerPart,
    index: number,
    amountParts: number
  ): SubProductDisplay {
    return standardStickerPart.productSize == null
      ? null
      : {
          header:
            amountParts === 1 ? 'Formaat: ' : `Formaat sticker ${index + 1}: `,
          line: ProductHelper.getWindowStickerSize(standardStickerPart)
        };
  }

  private static getStandardStickerPartDescriptionDisplay(
    standardStickerPart: StandardStickerPart,
    index: number,
    amountParts: number
  ): SubProductDisplay {
    return StringHelper.isEmpty(standardStickerPart.layoutDescription)
      ? null
      : {
          header:
            amountParts === 1
              ? 'Extra info: '
              : `Extra info sticker ${index + 1}: `,
          line: standardStickerPart.layoutDescription
        };
  }

  private static getCustomWindowStickerSubProductDisplays(
    customWindowSticker: CustomWindowsticker
  ): SubProductDisplay[] {
    return !CollectionHelper.isPopulated(customWindowSticker.stickerParts)
      ? []
      : customWindowSticker.stickerParts
          .map((sp, i) => {
            return [
              this.getStickerPartFormatDisplay(
                sp,
                i,
                customWindowSticker.stickerParts.length
              ),
              this.getStickerPartDescriptionDisplay(
                sp,
                i,
                customWindowSticker.stickerParts.length
              )
            ];
          })
          .flat();
  }

  private static getStickerPartFormatDisplay(
    stickerPart: StickerPart,
    index: number,
    amountParts: number
  ): SubProductDisplay {
    return stickerPart.stickerHeight == null || stickerPart.stickerWidth == null
      ? null
      : {
          header:
            amountParts === 1 ? 'Formaat: ' : `Formaat sticker ${index + 1}: `,
          line: ProductHelper.getCustomWindowStickerSize(stickerPart)
        };
  }

  private static getStickerPartDescriptionDisplay(
    stickerPart: StickerPart,
    index: number,
    amountParts: number
  ): SubProductDisplay {
    return StringHelper.isEmpty(stickerPart.description)
      ? null
      : {
          header:
            amountParts === 1
              ? 'Extra info: '
              : `Extra info sticker ${index + 1}: `,
          line: stickerPart.description
        };
  }

  private static getBoardSubProductDisplays(
    board: Board,
    full: boolean
  ): SubProductDisplay[] {
    return !CollectionHelper.isPopulated(board.boardParts)
      ? []
      : board.boardParts
          .map((bp, i) => {
            const amountParts = board.boardParts.length;
            return [
              this.getBoardPartFormatDisplay(bp, i, amountParts),
              this.getBoardPartMaterialDisplay(bp, i, amountParts),
              full &&
              BoardPartFormHelper.calculateShowDistanceToBottomSelector(bp)
                ? this.getBoardPartDistanceToBottomDisplay(bp, i, amountParts)
                : null,
              full
                ? this.getBoardPartPaintedPostsDisplay(bp, i, amountParts)
                : null,
              full
                ? this.getBoardPartDescriptionDisplay(bp, i, amountParts)
                : null
            ];
          })
          .flat();
  }

  private static getBoardPartMaterialDisplay(
    boardPart: BoardPart,
    index: number,
    amountParts: number
  ) {
    return boardPart.material == null
      ? null
      : {
          header:
            amountParts === 1 ? 'Materiaal: ' : `Materiaal bord ${index + 1}: `,
          line: ProductHelper.calculateMaterialDisplay(boardPart)
        };
  }

  private static getBoardPartFormatDisplay(
    boardPart: BoardPart,
    index: number,
    amountParts: number
  ): SubProductDisplay {
    return boardPart.height == null &&
      boardPart.width == null &&
      boardPart.boardType == null &&
      boardPart.placementType == null
      ? null
      : {
          header:
            amountParts === 1 ? 'Formaat: ' : `Formaat bord ${index + 1}: `,
          line: StringHelper.join(
            [
              ProductHelper.getBoardSize(boardPart),
              boardPart.boardType == null ? null : boardPart.boardType.label,
              boardPart.placementType == null
                ? null
                : boardPart.placementType.label
            ],
            ' - '
          )
        };
  }

  private static getBoardPartDistanceToBottomDisplay(
    boardPart: BoardPart,
    index: number,
    amountParts: number
  ) {
    return {
      header:
        amountParts === 1
          ? 'Afstand tot onderkant bord: '
          : `Afstand tot onderkant bord ${index + 1}: `,
      line: ProductHelper.calculateDistanceToBottomDisplay(boardPart)
    };
  }

  private static getBoardPartPaintedPostsDisplay(
    boardPart: BoardPart,
    index: number,
    amountParts: number
  ): SubProductDisplay {
    const line = ProductHelper.calculatePaintedPostsDisplay(boardPart);
    return line == null
      ? null
      : {
          header:
            amountParts === 1
              ? 'Geschilderde constructie: '
              : `Geschilderde constructie bord ${index + 1}: `,
          line: line
        };
  }

  private static getBoardPartDescriptionDisplay(
    boardPart: BoardPart,
    index: number,
    amountParts: number
  ): SubProductDisplay {
    return StringHelper.isEmpty(boardPart.layoutDescription)
      ? null
      : {
          header:
            amountParts === 1
              ? 'Extra info: '
              : `Extra info bord ${index + 1}: `,
          line: boardPart.layoutDescription
        };
  }

  private static getOtherProductSubProductDisplays(
    otherProduct: OtherProduct
  ): SubProductDisplay[] {
    return [
      this.getOtherProductFormatDisplay(otherProduct),
      this.getOtherProductDescriptionDisplay(otherProduct)
    ];
  }

  private static getOtherProductFormatDisplay(
    otherProduct: OtherProduct
  ): SubProductDisplay {
    return otherProduct.height == null && otherProduct.width == null
      ? null
      : {
          header: 'Formaat: ',
          line: ProductHelper.getOtherProductSize(otherProduct)
        };
  }

  private static getOtherProductDescriptionDisplay(
    otherProduct: OtherProduct
  ): SubProductDisplay {
    return StringHelper.isEmpty(otherProduct.layoutDescription)
      ? null
      : {
          header: 'Extra info',
          line: otherProduct.layoutDescription
        };
  }
}
